import { useCallback, useRef, useState } from "react";
import { isPanelKind } from "@bash/shared";
import type {
  ComposerPanel,
  ComposerPanelKind,
  RouterAction,
  RouterEvent,
  SurfaceId,
  ToolName,
} from "@bash/shared";
import { converseStream } from "@/lib/composer/converse-client";
import { useModelStore } from "@/stores/modelStore";

type ConversePhase =
  | { phase: "idle" }
  | { phase: "thinking" }
  | { phase: "answering"; text: string }
  | { phase: "routing"; tool: ToolName; text: string }
  | { phase: "answered"; text: string }
  | { phase: "routed"; action: RouterAction }
  | { phase: "error"; message: string }
  /** An empty wallet (founder 09-02): the door is Upgrade, never a retry. */
  | { phase: "credits"; message: string };

/**
 * The phase, plus the panels the turn put up (composer panels, spec
 * 2026-09-19; ruling d): keyed by kind, a later sheet replaces the earlier,
 * carried across every later phase, and ABSENT until one arrives, so a turn
 * with no panel produces exactly the shapes it always did.
 */
export type ConverseUiState = ConversePhase & { panels?: ComposerPanel[] };

/** Today's reduction, untouched: the phase alone. */
function reducePhase(state: ConverseUiState, ev: RouterEvent): ConverseUiState {
  switch (ev.type) {
    case "thread":
      return state;
    case "text_delta": {
      const prev =
        state.phase === "answering" || state.phase === "routing"
          ? state.text
          : "";
      if (state.phase === "routing")
        return { phase: "routing", tool: state.tool, text: prev + ev.text };
      return { phase: "answering", text: prev + ev.text };
    }
    case "tool_started": {
      const text = state.phase === "answering" ? state.text : "";
      return { phase: "routing", tool: ev.tool, text };
    }
    case "action":
      return { phase: "routed", action: ev.action };
    case "error":
      // An empty wallet is its own state: the person needs the door to top up,
      // not "Not sent" with a retry the server would refuse again.
      return ev.code === "credits_exhausted"
        ? { phase: "credits", message: ev.message }
        : { phase: "error", message: ev.message };
    case "done": {
      if (
        state.phase === "routed" ||
        state.phase === "error" ||
        state.phase === "credits"
      )
        return state;
      if (ev.reason === "answered") {
        const text =
          state.phase === "answering" || state.phase === "routing"
            ? state.text
            : "";
        return { phase: "answered", text };
      }
      return state;
    }
    default:
      return state;
  }
}

function withoutPanels(state: ConverseUiState): ConverseUiState {
  if (!state.panels) return state;
  const next = { ...state };
  delete next.panels;
  return next;
}

/** A panel frame: an unknown kind is ignored, close clears, a later panel of a kind replaces the earlier. */
export function withPanel(
  state: ConverseUiState,
  panel: ComposerPanel
): ConverseUiState {
  if (!isPanelKind(panel.kind as unknown)) return state;
  if (panel.kind === "close") return withoutPanels(state);
  const kept = (state.panels ?? []).filter((p) => p.kind !== panel.kind);
  return { ...state, panels: [...kept, panel] };
}

/** The host's × on one panel. */
export function dropPanel(
  state: ConverseUiState,
  kind: ComposerPanelKind
): ConverseUiState {
  const had = state.panels ?? [];
  const kept = had.filter((p) => p.kind !== kind);
  if (kept.length === had.length) return state;
  if (kept.length === 0) return withoutPanels(state);
  return { ...state, panels: kept };
}

/** Reduce one RouterEvent into the next UI state. `thread` is handled out-of-band. */
export function reduceEvent(
  state: ConverseUiState,
  ev: RouterEvent
): ConverseUiState {
  if (ev.type === "panel") return withPanel(state, ev.panel);
  const next = reducePhase(state, ev);
  // The panels ride every phase change: a sheet that rose before the words stays under them.
  return next === state || !state.panels
    ? next
    : { ...next, panels: state.panels };
}

export interface UseConverseArgs {
  surface: SurfaceId;
  getThreadId: () => string | undefined;
  onThreadId: (id: string) => void;
  getActiveTaskId: () => string | undefined;
}

export interface UseConverseResult {
  state: ConverseUiState;
  send: (input: string) => Promise<void>;
  reset: () => void;
  abort: () => void;
}

export function useConverse(args: UseConverseArgs): UseConverseResult {
  const [state, setState] = useState<ConverseUiState>({ phase: "idle" });
  const acRef = useRef<AbortController | null>(null);
  const argsRef = useRef(args);
  argsRef.current = args;

  const abort = useCallback((): void => {
    acRef.current?.abort();
    acRef.current = null;
  }, []);
  const reset = useCallback((): void => {
    abort();
    setState({ phase: "idle" });
  }, [abort]);

  const send = useCallback(
    async (input: string): Promise<void> => {
      const a = argsRef.current;
      const text = input.trim();
      if (!text) return;
      abort();
      const ac = new AbortController();
      acRef.current = ac;
      setState({ phase: "thinking" });
      const activeTaskId = a.getActiveTaskId();
      const req = {
        input: text,
        surface: a.surface,
        threadId: a.getThreadId(),
        idempotencyKey: crypto.randomUUID(),
        context: activeTaskId ? { activeTaskId } : undefined,
        // The pill's pick rides along so a spawned task runs on it (founder
        // ruling 07-16: explicit pick beats the server's model-policy ladder).
        // Same threading as useComposerConverse — ⌘K omitting it silently
        // downgraded an explicit pick (07-28).
        taskModel: useModelStore.getState().model,
      };
      let acc: ConverseUiState = { phase: "thinking" };
      for await (const ev of converseStream(req, {
        signal: ac.signal,
        stream: true,
      })) {
        if (ev.type === "thread") {
          a.onThreadId(ev.threadId);
          continue;
        }
        acc = reduceEvent(acc, ev);
        setState(acc);
      }
    },
    [abort]
  );

  return { state, send, reset, abort };
}
